import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import clearProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/ClearProduct";
import reset from "@insite/client-framework/Store/Components/ProductSelector/Handlers/Reset";
import searchProducts from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SearchProducts";
import setProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetProduct";
import setUnitOfMeasure from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetUnitOfMeasure";
import { getProductSelector } from "@insite/client-framework/Store/Components/ProductSelector/ProductSelectorSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XCSearchTextField from "@midland-industries/Widgets/Common/XCSearchTextField";
import debounce from "lodash/debounce";
import React, { ChangeEvent, ReactNode, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* CHANGELOG
    - 20230911:
        Created from ProductSelector.
        Added function to click the 'Add to Cart' button when enter is pressed
        in the Qty field if everything is valid.
    - 20231114:
        Implemented XCSearchTextField instead of DynamicDropdown widget.
*/

interface OwnProps {
    onSelectProduct: (productInfo: ProductInfo, product: ProductModel) => void;
    skipInventoryValidation?: boolean;
    denyFixedConfiguration?: boolean;
    selectButtonTitle?: string;
    productIsConfigurableMessage?: ReactNode;
    productIsUnavailableMessage?: ReactNode;
    customErrorMessage?: ReactNode;
    extendedStyles?: ProductSelectorStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const { isSearching, searchResults, selectedProductInfo, errorType } = getProductSelector(state);
    const product = getProductState(state, selectedProductInfo?.productId).value;
    return {
        isSearching,
        searchResults,
        selectedProductInfo,
        product,
        errorType,
        location: getLocation(state),
    };
};

const mapDispatchToProps = {
    searchProducts,
    setProduct,
    clearProduct,
    setUnitOfMeasure,
    reset,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductSelectorStyles {
    container?: GridContainerProps;
    searchGridItem?: GridItemProps;
    qtyGridItem?: GridItemProps;
    unitOfMeasureGridItem?: GridItemProps;
    buttonGridItem?: GridItemProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    qtyTextField?: TextFieldPresentationProps;
    optionWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    productImage?: LazyImageProps;
    infoWrapper?: InjectableCss;
    autocompleteTitleText?: TypographyPresentationProps;
    autocompleteErpText?: TypographyPresentationProps;
    selectButton?: ButtonPresentationProps;
    searchDynamicDropdown?: DynamicDropdownPresentationProps;
    clearIcon?: IconProps;
}

export const productSelectorStyles: ProductSelectorStyles = {
    container: {
        gap: 10,
    },
    searchGridItem: {
        width: [12, 12, 4, 5, 5],
        css: css`
            position: relative;
            display: flex;
            flex-direction: column;
        `,
    },
    qtyGridItem: {
        width: [3, 3, 2, 1, 1],
    },
    unitOfMeasureGridItem: {
        width: [9, 9, 3, 3, 3],
    },
    buttonGridItem: {
        width: [12, 12, 3, 3, 3],
        css: css`
            position: relative;
            display: flex;
            flex-direction: column;
            justify-content: flex-start !important;
        `,
    },
    optionWrapper: {
        css: css`
            display: flex;
            cursor: pointer;
        `,
    },
    imageWrapper: {
        css: css`
            display: flex;
            width: 50px;
            height: 50px;
            margin-right: 10px;
        `,
    },
    productImage: {
        width: "50px",
        css: css`
            flex-shrink: 0;
            img {
                height: 100%;
            }
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    autocompleteTitleText: {
        size: 14,
    },
    autocompleteErpText: {
        size: 14,
        css: css`
            margin-top: 5px;
        `,
    },
    selectButton: {
        css: css`
            margin-top: 30px;
            &:focus {
                outline: 3px solid #09f;
                outline-offset: 3px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 100%;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    clearIcon: {
        css: css`
            position: absolute;
            right: 40px;
            top: 45px;
            cursor: pointer;
        `,
    },
};

const TAB_KEY = 9;
const ENTER_KEY = 13;

const ProductSelector = ({
    onSelectProduct,
    skipInventoryValidation,
    denyFixedConfiguration,
    selectButtonTitle,
    searchProducts,
    searchResults,
    setProduct,
    clearProduct,
    setUnitOfMeasure,
    selectedProductInfo,
    product,
    errorType,
    extendedStyles,
    customErrorMessage,
    productIsConfigurableMessage,
    productIsUnavailableMessage,
    location,
    reset,
}: Props) => {
    const [qty, setQty] = useState("1");
    const [qtyHasChanged, setQtyHasChanged] = useState(false);
    const [errorMessage, setErrorMessage] = useState<ReactNode>("");
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [styles] = useState(() => mergeToNew(productSelectorStyles, extendedStyles));
    const [searching, setSearching] = useState(false);

    useEffect(() => {
        reset();
    }, [location.pathname]);

    /**
     * When new searchResults are received, determine if there is a match or an error.
     */
    useEffect(() => {
        if (!searchResults) {
            return;
        }

        if (!product) {
            if (searchTerm === "") {
                return;
            }
            if (!searchResults) {
                return;
            }

            let matchId: string | null = null;

            // Check to see if we got an exact match on the erpNumber.
            const erpNumberMatch = searchResults.find(
                option => option.erpNumber.toLowerCase() === searchTerm.toLowerCase(),
            );
            if (erpNumberMatch) {
                if (erpNumberMatch.id) {
                    // onSelectionChangeHandler(erpNumberMatch.id);
                    // return;
                    matchId = erpNumberMatch.id;
                }
            }

            if (!matchId) {
                // Check to see if we got an exact match on the name (Customer Part Number).
                const nameMatch = searchResults.find(option => option.name.toLowerCase() === searchTerm.toLowerCase());
                if (nameMatch) {
                    if (nameMatch.id) {
                        // onSelectionChangeHandler(nameMatch.id);
                        // return;
                        matchId = nameMatch.id;
                    }
                }
            }

            // Set error and bail out if no match found.
            if (!matchId) {
                setErrorMessage("Product not found");
                onCompleteSearch();
                return;
            }

            // Set the Product
            const variantParentId = searchResults.find(o => o.id === searchTerm)?.styleParentId;
            if (variantParentId) {
                setProduct({
                    productId: variantParentId,
                    variantId: matchId,
                    validateProduct: true,
                    skipInventoryValidation,
                    denyFixedConfiguration,
                });
            } else {
                setProduct({
                    productId: matchId,
                    validateProduct: true,
                    skipInventoryValidation,
                    denyFixedConfiguration,
                });
            }
        }
    }, [searchResults]);

    useEffect(() => {
        setErrorMessage(customErrorMessage);
    }, [customErrorMessage]);

    /**
     * When the selectedProductInfo changes, update the selectedProductId and Qty values.
     */
    useEffect(() => {
        if (selectedProductInfo && !qtyHasChanged) {
            setQty(`${selectedProductInfo.qtyOrdered}`);
        }

        if (selectedProductInfo) {
            addToCart();
            onCompleteSearch();
        }
    }, [selectedProductInfo]);

    useEffect(() => {
        if (!searching) {
            setFocusOnProductSelectorInputElement();
        }
    }, [searching]);

    useEffect(() => {
        switch (errorType) {
            case "productIsConfigurable":
                setErrorMessage(productIsConfigurableMessage || translate("Cannot select configurable products"));
                break;
            case "productIsUnavailable":
                setErrorMessage(productIsUnavailableMessage || translate("Product is unavailable"));
                break;
            default:
                setErrorMessage("");
                break;
        }
    }, [errorType]);

    /**
     * When ENTER is pressed in XCSearchTextField, set the focus on the QTY field.
     * @param event onKeyDown event from XCSearchTextField.
     */
    const onKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        event.stopPropagation();
        switch (event.keyCode) {
            case ENTER_KEY:
                event.preventDefault();
                setFocusOnQtyInputElement();
                break;
            default:
                break;
        }
    };

    /**
     * Now being done when exact match is found in useEffect[searchResults].
     * Called when search results are received and an exact match Product is found.
     */
    /* const onSelectionChangeHandler = (value?: string) => {
        if (searchResults && searchResults.length > 0) {
            const variantParentId = searchResults.find(o => o.id === value)?.styleParentId;
            if (variantParentId) {
                setProduct({
                    productId: variantParentId,
                    variantId: value,
                    validateProduct: true,
                    skipInventoryValidation,
                    denyFixedConfiguration,
                });
            } else {
                setProduct({
                    productId: value,
                    validateProduct: true,
                    skipInventoryValidation,
                    denyFixedConfiguration,
                });
            }
            // setFocusOnQtyInputElement();
        }
    }; */

    /**
     * Triggered by the user when they click 'Enter' in the search textField.
     */
    const onBeginSearch = () => {
        setSearching(true);
        debouncedSearchProducts(searchTerm);
    };

    const onCompleteSearch = () => {
        setSearching(false);
    };

    /**
     * Performs the search
     */
    const debouncedSearchProducts = debounce((query: string) => {
        searchProducts({ query });
    }, 300);

    /**
     * When the searchTerm input field is changed, update the searchTerm.
     * This function will trigger every time the searchTerm is modified on the child.
     * @param event The onChange event sent from the child.
     */
    const onInputChanged = (event: ChangeEvent<HTMLInputElement>) => {
        setErrorMessage("");
        setSearchTerm(event.target.value);

        if (selectedProductInfo) {
            clearProduct();
        }
    };

    const setFocusOnProductSelectorInputElement = () => {
        const inputElement = document.querySelectorAll<HTMLInputElement>(
            'input[data-test-selector="productSelector_search-input"]',
        )[0];
        inputElement?.focus();
    };

    const setFocusOnQtyInputElement = () => {
        const inputElement = document.querySelectorAll<HTMLInputElement>(
            'input[data-test-selector="productSelector_qty"]',
        )[0];
        inputElement?.focus();
    };

    const uomChangeHandler = (unitOfMeasure: string) => {
        if (!selectedProductInfo) {
            return;
        }

        setUnitOfMeasure({ unitOfMeasure });
    };

    /**
     * This method is called when the user removes a selected Product from the search field
     * by clicking on the X icon.
     */
    const clearButtonHandler = () => {
        clearProduct();
        setSearchTerm("");
        setQty("1");
        setQtyHasChanged(false);
        searchProducts({ query: "" });
    };

    /**
     * Adds the item to the cart when enter is pressed in the QTY field.
     */
    const onKeyPressHandlerQty = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const invalidRegex = new RegExp("[.,+*-/]");
        const res = invalidRegex.test(event.key);
        if (res) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (event.which === 13) {
            // const hasSelectedProduct = !(!selectedProductInfo);
            // const hasValidQty = Number(qty) > 0;
            // const hasNoErrors = !(errorMessage);
            // const valid = hasSelectedProduct && hasValidQty && hasNoErrors;

            // if (valid) {
            //     selectProduct();
            // }

            // (event.target as any).blur();
            selectProduct();
        }
    };

    /**
     * Called when adding product to the cart.
     */
    const selectProduct = () => {
        onBeginSearch();
    };

    const addToCart = () => {
        if (!selectedProductInfo || errorMessage || !product) {
            return;
        }
        if (Number(qty) < 1) {
            return;
        }

        const newProductInfo = { ...selectedProductInfo };
        newProductInfo.qtyOrdered = parseFloat(qty);

        onSelectProduct(newProductInfo, product);
        clearProduct();
        setQty("1");
        setQtyHasChanged(false);
        searchProducts({ query: "" });
        setSearchTerm("");
    };

    return (
        <GridContainer {...styles.container} data-test-selector="productSelector">
            <GridItem {...styles.searchGridItem}>
                <XCSearchTextField
                    label={translate("Search")}
                    value={searchTerm}
                    errorMessage={errorMessage}
                    onKeyDown={onKeyDownHandler}
                    onChange={onInputChanged}
                    onBeginSearch={onBeginSearch}
                    searching={searching}
                    dataTestSelector="productSelector_search-input"
                />
            </GridItem>
            <GridItem {...styles.qtyGridItem}>
                <TextField
                    type="number"
                    min="0"
                    label={translate("QTY")}
                    value={qty}
                    onKeyPress={onKeyPressHandlerQty}
                    onChange={e => {
                        setQty(e.currentTarget.value);
                        setQtyHasChanged(true);
                    }}
                    disabled={searching}
                    data-test-selector="productSelector_qty"
                />
            </GridItem>
            <GridItem {...styles.unitOfMeasureGridItem}>
                {product && selectedProductInfo && (
                    <ProductContext.Provider
                        value={{ product, productInfo: selectedProductInfo, onUnitOfMeasureChanged: uomChangeHandler }}
                    >
                        <ProductUnitOfMeasureSelect
                            extendedStyles={styles.unitOfMeasureSelect}
                            data-test-selector="productSelector_uom"
                        />
                    </ProductContext.Provider>
                )}
            </GridItem>
            <GridItem {...styles.buttonGridItem}>
                <Button
                    {...styles.selectButton}
                    onClick={() => selectProduct()}
                    // disabled={!selectedProductInfo || !!errorMessage || Number(qty) < 1}
                    disabled={searchTerm.length === 0 || Number(qty) < 1 || searching}
                    data-test-selector="productSelector_selectProduct"
                >
                    {selectButtonTitle || translate("Select Product")}
                </Button>
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductSelector);
